// ============================================================================
// /leaderboard — public Strava-like top 100 traders by TDI score.
// Anyone (incl. anonymous) can view. Each row is anonymized (first name +
// last initial) so users get social-proof status without leaking PII.
//
// Viral loop: people screenshot their position + share to Twitter/IG.
// "I just hit top 20 disciplined traders on SnapChart AI." → CTA bait.
// ============================================================================
import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { supabase } from '@/integrations/supabase/client';
import SafeIcon from '../components/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import logoSrc from '@/assets/snap-chart-logo-final.png';

const { FiArrowRight, FiShield, FiTrendingUp, FiZap, FiAward } = FiIcons;

const TIER_COLOR = {
  ELITE:       { hex: '#10b981', chip: 'bg-emerald-500/15 border-emerald-500/30 text-emerald-300' },
  STRONG:      { hex: '#22d3ee', chip: 'bg-cyan-500/15 border-cyan-500/30 text-cyan-300' },
  DEVELOPING:  { hex: '#a855f7', chip: 'bg-purple-500/15 border-purple-500/30 text-purple-300' },
  REBUILDING:  { hex: '#f59e0b', chip: 'bg-amber-500/15 border-amber-500/30 text-amber-300' },
};

const fmtUsd = (n) => `$${Number(n || 0).toLocaleString()}`;

const Leaderboard = () => {
  const [rows, setRows] = useState([]);
  const [totalUsers, setTotalUsers] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data, error: e } = await supabase.rpc('get_public_tdi_leaderboard', { p_limit: 100 });
        if (cancelled) return;
        if (e) { setError(e.message); return; }
        if (data && typeof data === 'object') {
          setRows(Array.isArray(data.leaderboard) ? data.leaderboard : []);
          setTotalUsers(data.total_qualifying_users || 0);
        }
      } catch (err) {
        if (!cancelled) setError(err?.message || 'Failed to load');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-900 via-gray-900 to-black text-white pt-8 pb-24 px-4 sm:px-6 lg:px-8">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="text-center mb-10">
          <Link to="/" className="inline-flex items-center gap-2 mb-6">
            <img src={logoSrc} alt="" className="h-8 w-8" />
            <span className="text-sm font-bold bg-gradient-to-r from-purple-400 to-cyan-400 bg-clip-text text-transparent">SnapChart AI</span>
          </Link>
          <span className="inline-block px-3 py-1 rounded-full bg-cyan-500/10 border border-cyan-500/20 text-[10px] font-mono uppercase tracking-[0.25em] text-cyan-300 mb-4">
            top 100 · disciplined traders
          </span>
          <h1 className="text-4xl sm:text-5xl md:text-6xl font-black tracking-tight leading-[1.05] mb-3">
            <span className="text-white">The Discipline </span>
            <span className="bg-gradient-to-r from-purple-400 via-cyan-300 to-cyan-400 bg-clip-text text-transparent">Leaderboard</span>
          </h1>
          <p className="text-base sm:text-lg text-gray-400 max-w-2xl mx-auto">
            Ranked by TDI (Trader Discipline Index) — FICO-style score from 300 to 1000.
            <br className="hidden md:block" />
            Capital preserved by NOT trading impulsively. {totalUsers > 0 && <span className="text-cyan-400 font-semibold">{totalUsers} qualifying traders</span>}.
          </p>
        </div>

        {/* Leaderboard table */}
        {loading ? (
          <div className="text-center py-20 text-gray-500 font-mono uppercase tracking-[0.2em] text-sm">
            loading…
          </div>
        ) : error ? (
          <div className="text-center py-20">
            <p className="text-red-300 mb-4">Could not load leaderboard.</p>
            <p className="text-xs text-gray-500 font-mono">{error}</p>
          </div>
        ) : rows.length === 0 ? (
          <div className="text-center py-20 max-w-md mx-auto">
            <SafeIcon icon={FiAward} className="text-cyan-400 text-5xl mx-auto mb-4" />
            <p className="text-lg text-gray-300 mb-2">The leaderboard is empty — for now.</p>
            <p className="text-sm text-gray-500 mb-6">
              Be the first to qualify. Run 5 analyses and your TDI score appears here.
            </p>
            <Link to="/register"
              className="inline-flex items-center gap-2 px-6 py-3 rounded-xl bg-gradient-to-r from-cyan-400 to-purple-400 text-black font-bold hover:scale-105 transition shadow-xl shadow-cyan-500/30">
              Start free trial <SafeIcon icon={FiArrowRight} />
            </Link>
          </div>
        ) : (
          <>
            {/* Top 3 podium */}
            {rows.length >= 3 && (
              <div className="grid grid-cols-1 xs:grid-cols-3 sm:grid-cols-3 gap-3 mb-6 max-w-3xl mx-auto">
                {[1, 0, 2].map((idx) => {
                  const r = rows[idx];
                  const color = TIER_COLOR[r.tier] || TIER_COLOR.DEVELOPING;
                  const isFirst = idx === 0;
                  return (
                    <motion.div key={r.rank}
                      initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: idx * 0.08 }}
                      className={`relative rounded-2xl border-2 p-4 text-center ${isFirst ? 'sm:scale-110 sm:-translate-y-2' : ''}`}
                      style={{ borderColor: `${color.hex}66`, background: `linear-gradient(180deg, ${color.hex}1F 0%, transparent 100%)` }}>
                      <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-2.5 py-0.5 rounded-md text-[10px] font-mono uppercase tracking-[0.15em] font-bold"
                        style={{ background: color.hex, color: '#0a0e1a' }}>
                        #{r.rank}
                      </div>
                      <div className="text-3xl mb-1">{idx === 0 ? '🥇' : idx === 1 ? '🥈' : '🥉'}</div>
                      <div className="text-xs font-semibold text-white truncate mb-1" title={r.handle}>{r.handle}</div>
                      <div className="text-2xl font-black font-mono tabular-nums" style={{ color: color.hex }}>{r.tdi}</div>
                      <div className="text-[9px] font-mono uppercase tracking-wider text-gray-500 mt-0.5">{r.tier}</div>
                      <div className="text-[10px] text-gray-500 mt-1 font-mono">{fmtUsd(r.capital)} saved</div>
                    </motion.div>
                  );
                })}
              </div>
            )}

            {/* Rest of the leaderboard */}
            <div className="rounded-2xl border border-white/10 bg-gray-900/40 overflow-hidden">
              <div className="grid grid-cols-12 gap-2 px-4 py-3 border-b border-white/5 text-[9px] font-mono uppercase tracking-[0.2em] text-gray-500">
                <div className="col-span-1">rank</div>
                <div className="col-span-5 sm:col-span-4">trader</div>
                <div className="col-span-3 sm:col-span-2 text-right">tdi</div>
                <div className="col-span-3 sm:col-span-2 text-right">analyses</div>
                <div className="hidden sm:block col-span-3 text-right">capital saved</div>
              </div>
              {rows.slice(rows.length >= 3 ? 3 : 0).map((r, i) => {
                const color = TIER_COLOR[r.tier] || TIER_COLOR.DEVELOPING;
                return (
                  <motion.div key={r.rank}
                    initial={{ opacity: 0, x: -8 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.3 + i * 0.015 }}
                    className="grid grid-cols-12 gap-2 px-4 py-3 border-b border-white/5 hover:bg-white/[0.02] transition text-sm last:border-b-0">
                    <div className="col-span-1 font-mono text-gray-500 tabular-nums">#{r.rank}</div>
                    <div className="col-span-5 sm:col-span-4 flex items-center gap-2 truncate">
                      <span className="text-white truncate font-medium" title={r.handle}>{r.handle}</span>
                      <span className={`hidden sm:inline-flex text-[9px] font-mono uppercase tracking-wider px-1.5 py-0.5 rounded ${color.chip}`}>
                        {r.tier}
                      </span>
                    </div>
                    <div className="col-span-3 sm:col-span-2 text-right font-mono font-bold tabular-nums" style={{ color: color.hex }}>
                      {r.tdi}
                    </div>
                    <div className="col-span-3 sm:col-span-2 text-right font-mono text-gray-400 tabular-nums">
                      {r.analyses}
                    </div>
                    <div className="hidden sm:block col-span-3 text-right font-mono text-emerald-300 tabular-nums">
                      {fmtUsd(r.capital)}
                    </div>
                  </motion.div>
                );
              })}
            </div>

            {/* CTA bottom */}
            <div className="mt-10 text-center">
              <p className="text-sm text-gray-400 mb-4">
                Where do you rank? Take the free test or start analyzing today.
              </p>
              <div className="flex flex-col sm:flex-row items-center justify-center gap-2">
                <Link to="/test"
                  className="inline-flex items-center gap-2 px-5 py-3 rounded-xl border border-amber-400/30 bg-amber-500/5 text-amber-300 hover:bg-amber-500/10 transition font-bold text-sm">
                  ▸ Run the free test
                </Link>
                <Link to="/register"
                  className="inline-flex items-center gap-2 px-5 py-3 rounded-xl bg-gradient-to-r from-cyan-400 to-purple-400 text-black font-bold text-sm hover:scale-105 transition shadow-xl shadow-cyan-500/30">
                  Start free trial <SafeIcon icon={FiArrowRight} />
                </Link>
              </div>
            </div>
          </>
        )}

        {/* Footer note — methodology kept private (proprietary algorithm).
            Don't leak the TDI formula publicly so competitors can't reverse-engineer it. */}
        <div className="mt-16 pt-8 border-t border-white/5 text-center">
          <p className="text-xs text-gray-500 font-mono leading-relaxed max-w-2xl mx-auto">
            <span className="text-gray-400">TDI</span> · Proprietary discipline index combining win rate, capital preserved, and decision consistency.
            Minimum 5 analyses to qualify. Handles anonymized. Updated continuously.
          </p>
        </div>
      </div>
    </div>
  );
};

export default Leaderboard;
